import {register} from '@shopify/theme-sections';
import Flickity from 'flickity';

import 'flickity-imagesloaded';
import {updateOffscreenPanelVisibility} from '../utils';

const selectors = {
  sliderContainer: '[data-slider-container]',
  slide: '[data-slide]',
  prevButton: '[data-prev-btn]',
  nextButton: '[data-next-btn]',
  video: '[data-background-video]',
};

const classes = {
  singleSlide: 'single-slide',
  selectedSlide: 'is-selected',
  videoPlaying: 'video-playing',
  isVisible: 'is-visible',
  heroSliderFull: 'hero-slider--full',
  fullWidthMedia: 'hero-slide--media-full',
};

const sectionType = 'hero-slider';
const registeredSections = window.Shopify?.theme?.sections?.registered || {};
const isUnregistered = typeof registeredSections[sectionType] === 'undefined';

export default isUnregistered &&
  register(sectionType, {
    onLoad() {
      const container = this.container;
      const sectionSettings = container.dataset.sectionSettings || '{}';
      this.settings = JSON.parse(sectionSettings);

      const slider = container.querySelector(selectors.sliderContainer);
      if (!slider) return;

      const heroSlides = slider.querySelectorAll(selectors.slide);
      const previousButton = container.querySelector(selectors.prevButton);
      const nextButton = container.querySelector(selectors.nextButton);

      const handleVideo = async (index) => {
        const videoWrapper = heroSlides[index]?.querySelector(selectors.video);

        if (videoWrapper === null) return;
        const video = videoWrapper.querySelector('video');
        await video.play();

        videoWrapper.classList.add(classes.videoPlaying);
      };

      const autoplayDotAnimation = () => {
        const thisFlickity = Flickity.data(slider);
        if (!thisFlickity) return;
        const {autoPlay} = thisFlickity.options;
        if (!autoPlay) return;
        const autoplaySpeed = typeof autoPlay === 'boolean' ? '3000ms' : `${parseInt(autoPlay, 10)}ms`;
        document.documentElement.style.setProperty('--heroSliderAutoplaySpeed', autoplaySpeed);
      };

      const handleMouseOver = () => {
        const thisFlickity = Flickity.data(slider);
        if (!thisFlickity) return;
        const {autoPlay} = thisFlickity.options;
        if (!autoPlay) return;
        const pageDots = thisFlickity.pageDots;
        if (!pageDots) return;
        slider.addEventListener('mouseleave', () => {
          slider.classList.remove('autoplay-slider');
          // trigger reflow between class add/remove
          // eslint-disable-next-line babel/no-unused-expressions
          slider.offsetHeight;
          slider.classList.add('autoplay-slider');
        });
      };

      const setupHalfVideoPadding = () => {
        if (!slider.classList.contains(classes.heroSliderFull)) return;
        const videoSlides = Array.from(slider.querySelectorAll(selectors.video));
        if (!videoSlides.length) return;
        const halfWidthVideoSlides = videoSlides.filter(
          (slide) => !slide.parentNode.classList.contains(classes.fullWidthMedia)
        );
        if (!halfWidthVideoSlides.length) return;

        const applyNewRatioPadding = (videoSlide) => {
          const desktopQuery = window.matchMedia(`(min-width: ${window.theme.breakpoints.medium}px)`);
          if (!desktopQuery.matches) return;
          const componentWrapper = videoSlide.firstElementChild;
          const slideHeight = videoSlide.offsetHeight;
          const halfSlideWidth = videoSlide.offsetWidth / 2;
          const newPadding = ((Math.round(slideHeight) / Math.round(halfSlideWidth)) * 100).toFixed(2);
          componentWrapper.style.paddingTop = `${newPadding}%`;
        };

        halfWidthVideoSlides.forEach((videoSlide) => {
          applyNewRatioPadding(videoSlide);
        });

        window.addEventListener('resize', () => {
          halfWidthVideoSlides.forEach((videoSlide) => {
            applyNewRatioPadding(videoSlide);
          });
        });
      };

      if (slider.classList.contains(classes.singleSlide)) {
        const slide = container.querySelector(selectors.slide);
        slide?.classList.add(classes.selectedSlide);
        handleVideo(0);
        return;
      }

      this.flickity = new Flickity(slider, {
        prevNextButtons: false,
        autoPlay: this.settings.autoplay,
        pageDots: this.settings.include_pager_dots,
        adaptiveHeight: true,
        watchCSS: true,
        wrapAround: this.settings.infinite,
        on: {
          ready() {
            slider.classList.add(classes.isVisible);
            updateOffscreenPanelVisibility(heroSlides);
            handleVideo(0);
            autoplayDotAnimation();
            handleMouseOver();
            setupHalfVideoPadding();
          },
          change(index) {
            updateOffscreenPanelVisibility(heroSlides);
            handleVideo(index);
          },
        },
      });

      previousButton?.addEventListener('click', () => this.flickity.previous());
      nextButton?.addEventListener('click', () => this.flickity.next());
    },

    onBlockSelect(event) {
      // Only run in the admin theme customizer
      if (!Shopify.designMode) {
        return;
      }

      // Turn off autoplay when slide selected
      if (this.settings.autoplay) {
        this.flickity.pausePlayer();
      }

      // Scroll to selected slide
      const index = Array.from(event.target.parentElement.children).indexOf(event.target);
      this.flickity.select(index);
    },

    onDeselect() {
      // Only run in the admin theme customizer
      if (!Shopify.designMode) {
        return;
      }

      // Turn back on autoplay when slide deselected
      if (this.settings.autoplay) {
        this.flickity.unpausePlayer();
      }
    },
  });
